import { cn } from '@/lib/utils';
import {
	adminDashboardRows,
	getAdminDashboardCard,
} from '../adminDashboardConfig';
import { AdminDashboardGroupCard } from './AdminDashboardGroupCard';
import { AdminDashboardStatCard } from './AdminDashboardStatCard';

const columnClass: Record<2 | 3, string> = {
	2: 'grid-cols-2',
	3: 'grid-cols-1 sm:grid-cols-3',
};

const DashboardStatCardSkeleton = ({ grouped }: { grouped?: boolean }) => (
	<div
		className={cn(
			'animate-pulse rounded-[1.25rem] border border-stone-100 bg-white shadow-sm',
			grouped ? 'p-2' : 'p-4',
		)}
	>
		{grouped ? (
			<div className="space-y-2 p-2">
				{[0, 1].map((index) => (
					<div key={index} className="flex items-center gap-2.5">
						<div className="size-7 rounded-lg bg-stone-200" />
						<div className="h-3 flex-1 rounded bg-stone-200" />
					</div>
				))}
			</div>
		) : (
			<div className="flex items-center gap-2.5">
				<div className="size-10 rounded-lg bg-stone-200" />
				<div className="flex-1 space-y-1.5">
					<div className="h-4 w-24 rounded bg-stone-200" />
					<div className="h-6 w-12 rounded bg-stone-100" />
				</div>
			</div>
		)}
	</div>
);

type AdminDashboardGridProps = {
	indicators: Dashboard.Indicators | undefined;
	isLoading: boolean;
};

export const AdminDashboardGrid = ({
	indicators,
	isLoading,
}: AdminDashboardGridProps) => {
	if (isLoading || !indicators) {
		return (
			<div className="space-y-4">
				{adminDashboardRows.map((row) => (
					<div
						key={row.items.map((item) => item.key).join('-')}
						className={cn('grid gap-3', columnClass[row.columns])}
					>
						{row.items.map((item) => (
							<DashboardStatCardSkeleton
								key={item.key}
								grouped={item.type === 'group'}
							/>
						))}
					</div>
				))}
			</div>
		);
	}

	return (
		<div className="space-y-4">
			{adminDashboardRows.map((row) => (
				<div
					key={row.items.map((item) => item.key).join('-')}
					className={cn('grid gap-3', columnClass[row.columns])}
				>
					{row.items.map((item) => {
						if (item.type === 'group') {
							return (
								<AdminDashboardGroupCard
									key={item.key}
									keys={item.keys}
									indicators={indicators}
									getCard={getAdminDashboardCard}
								/>
							);
						}

						const config = getAdminDashboardCard(item.key);
						if (!config) return null;

						const stats = config.getStats?.(indicators) ?? null;

						return (
							<AdminDashboardStatCard
								key={item.key}
								config={config}
								size={row.size}
								stats={stats}
							/>
						);
					})}
				</div>
			))}
		</div>
	);
};
